import React, { useMemo, useState, forwardRef, useImperativeHandle, useCallback, useRef } from 'react';
import { StyleSheet } from 'react-native';
import { Canvas, Path, Skia } from '@shopify/react-native-skia';
import type { ArrowData } from '@components/Hierarchy/DraggableUserCard';
import { debounce } from 'lodash';

export interface HierarchyArrowRendererProps {
  arrowsRef: React.MutableRefObject<Map<string, ArrowData>>;  // Read from ref like LinkHandler
  width: number;
  height: number;
  zoomLevel?: number; // For zoom-proportionate updates
  zIndex?: number;
}

export interface HierarchyArrowRendererHandle {
  forceUpdate: () => void;
}

/**
 * Centralized arrow renderer using Skia Canvas
 * Reads arrows directly from ref (like LinkHandler pattern)
 * This component should be placed at the hierarchy container level
 * to render all arrows from UserComponents without clipping restrictions
 * 
 * CRITICAL: This component manages its OWN state to prevent parent re-renders
 */
const HierarchyArrowRenderer = forwardRef<HierarchyArrowRendererHandle, HierarchyArrowRendererProps>(({
  arrowsRef,
  width,
  height,
  zoomLevel = 1,
  zIndex = 1
}, ref) => {
  // State managed internally - does NOT affect parent
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const lastUpdateTimeRef = useRef(0);

  // Zoom-proportionate update throttling
  // Higher zoom = more updates (more detail visible)
  // Lower zoom = fewer updates (less detail needed)
  const getThrottleDelay = useCallback((zoom: number) => {
    // Zoom < 0.5: 16ms (60fps)
    // Zoom 0.5-1: 8ms (125fps)
    // Zoom > 1: 4ms (250fps)
    if (zoom > 1) return 4;
    if (zoom > 0.5) return 8;
    return 16;
  }, []);

  // Throttled update based on zoom level
  const throttledUpdate = useCallback(() => {
    const now = Date.now();
    const delay = getThrottleDelay(zoomLevel);
    
    if (now - lastUpdateTimeRef.current >= delay) {
      setUpdateTrigger(prev => prev + 1);
      lastUpdateTimeRef.current = now;
    }
  }, [zoomLevel, getThrottleDelay]);
  
  // Expose imperative handle for parent to trigger updates WITHOUT parent state
  useImperativeHandle(ref, () => ({
    forceUpdate: () => {
      throttledUpdate();
    }
  }), [throttledUpdate]);

  // Convert arrow data to Skia paths (read from ref like LinkHandler)
  const arrowPaths = useMemo(() => {
    const paths: Array<{
      id: string;
      curvePath: ReturnType<typeof Skia.Path.MakeFromSVGString> | null;
      arrowheadPath: ReturnType<typeof Skia.Path.Make>;
      color: string;
      startX: number;
      startY: number;
    }> = [];

    // Read directly from ref - always current!
    const currentArrows = arrowsRef.current;

    currentArrows.forEach((arrowData, id) => {
      try {
        // Skip reset/empty arrows
        if (arrowData.reset || arrowData.path === 'M0,0 L0,0') {
          return;
        }

        // Validate arrow data
        if (!arrowData.path || typeof arrowData.path !== 'string') {
          // console.warn('⚠️ Invalid arrow path for', id);
          return;
        }

        // Validate coordinates are valid numbers
        const isValidNumber = (n: number) => typeof n === 'number' && !isNaN(n) && isFinite(n);
        if (!isValidNumber(arrowData.startX) || !isValidNumber(arrowData.startY) ||
          !isValidNumber(arrowData.endX) || !isValidNumber(arrowData.endY)) {
          console.warn('⚠️ Invalid arrow coordinates for', id);
          return;
        }

        // Parse the curve path with error handling
        const curvePath = Skia.Path.MakeFromSVGString(arrowData.path);
        if (!curvePath) {
          // console.warn('⚠️ Failed to parse arrow path for', id);
          return;
        }

        // Create arrowhead triangle
        // CRITICAL: Arrowhead must be in RELATIVE coordinates (path is transformed by startX/startY)
        const arrowheadPath = Skia.Path.Make();
        const arrowSize = 20; // Smaller, cleaner arrowhead

        // Calculate RELATIVE endpoint (since path starts at M0,0 and is transformed)
        const relativeEndX = arrowData.endX - arrowData.startX;
        const relativeEndY = arrowData.endY - arrowData.startY;

        // Parse path to get second control point for accurate tangent angle
        // Path format: M0,0 C cp1x,cp1y cp2x,cp2y endx,endy
        let actualAngle = Math.atan2(relativeEndY, relativeEndX); // Fallback

        try {
          const pathMatch = arrowData.path.match(/C([\d.-]+),([\d.-]+)\s+([\d.-]+),([\d.-]+)\s+([\d.-]+),([\d.-]+)/);
          if (pathMatch) {
            const cp2X = parseFloat(pathMatch[3]);
            const cp2Y = parseFloat(pathMatch[4]);
            const endX = parseFloat(pathMatch[5]);
            const endY = parseFloat(pathMatch[6]);

            // Tangent at end = direction from second control point to endpoint
            actualAngle = Math.atan2(endY - cp2Y, endX - cp2X);
          }
        } catch (e) {
          // Use fallback angle calculation
        }

        // Arrowhead points in relative coordinates
        const tipX = relativeEndX;
        const tipY = relativeEndY;
        const baseAngle1 = actualAngle + (150 * Math.PI / 180);
        const baseAngle2 = actualAngle - (150 * Math.PI / 180);

        const base1X = tipX + arrowSize * Math.cos(baseAngle1);
        const base1Y = tipY + arrowSize * Math.sin(baseAngle1);
        const base2X = tipX + arrowSize * Math.cos(baseAngle2);
        const base2Y = tipY + arrowSize * Math.sin(baseAngle2);

        arrowheadPath.moveTo(tipX, tipY);
        arrowheadPath.lineTo(base1X, base1Y);
        arrowheadPath.lineTo(base2X, base2Y);
        arrowheadPath.close();

        paths.push({
          id,
          curvePath,
          arrowheadPath,
          color: arrowData.color || '#3b82f6',
          startX: arrowData.startX || 0,
          startY: arrowData.startY || 0,
        });
      } catch (error) {
        console.error('❌ Error processing arrow', id, error);
      }
    });

    // Only log if there are arrows (avoid spam)
    if (paths.length > 0) {
      // console.log('🎨 Rendered arrows:', paths.length);
    }
    return paths;
  }, [arrowsRef, updateTrigger]);  // Re-compute when updateTrigger changes (like LinkHandler)

  if (arrowPaths.length === 0) {
    return null;
  }

  return (
    <Canvas
      style={[
        styles.canvas,
        {
          width,
          height,
          zIndex,
        }
      ]}
      pointerEvents="none"
    >
      {arrowPaths.map(({ id, curvePath, arrowheadPath, color, startX, startY }) => {
        try {
          return (
            <React.Fragment key={id}>
              {/* Render the bezier curve */}
              {curvePath && (
                <Path
                  path={curvePath}
                  color={color}
                  style="stroke"
                  strokeWidth={4}
                  strokeCap="round"
                  strokeJoin="round"
                  transform={[{ translateX: startX }, { translateY: startY }]}
                />
              )}
              {/* Render the arrowhead */}
              <Path
                path={arrowheadPath}
                color={color}
                style="fill"
                transform={[{ translateX: startX }, { translateY: startY }]}
              />
            </React.Fragment>
          );
        } catch (error) {
          console.error('❌ Error rendering arrow', id, error);
          return null;
        }
      })}
    </Canvas>
  );
});

const styles = StyleSheet.create({
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

HierarchyArrowRenderer.displayName = 'HierarchyArrowRenderer';

export default HierarchyArrowRenderer;

